import { Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { SpaceListing } from '@dove/shared';
import { ApiError, suggestSpace } from '@/lib/api';
import { formatCount } from '@/lib/count';
import { useDebounced } from '@/lib/debounced';
import { useJoinedSpaces, useSpaceRooms } from '@/lib/membership';
import { spaceIcon } from '@/lib/space';
import { More } from '@/components/More';
import { SearchField } from '@/components/SearchField';
import { Button } from '@/components/ui/button';
import { Failed, Loading, Note } from '@/components/states';

/**
 * Looking a room up by name, and the list of rooms that answers.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS THE FIRST LOOKUP FIELD IN THE PRODUCT THAT REACHES SOMETHING YOU ARE
 * NOT ALREADY IN (docs/5 § D260803-1933), and the reason it is allowed is the whole of the
 * argument: **it searches rooms, and a room is a place rather than a person.**
 *
 * What docs/5 § D260730-2028 removed was a surface for browsing PEOPLE — a deck, a grid, a
 * lookup field over strangers — because a product that lets you shop for humans
 * gets a market in humans. None of that follows from a list of rooms: a room has
 * no photograph, no availability and no interest in you, joining one is not an
 * approach to anybody, and nobody is notified when you arrive. The Loft's field
 * (`LoftScreen`) stays what it was, a search over the people you already write
 * to, and no field anywhere accepts a handle.
 *
 * SO IT CANNOT BECOME PEOPLE SEARCH: the only thing `GET /spaces/search` matches on
 * is a room's title and slug, and it is the query layer that says so rather than this
 * component — the same place the seal is enforced, which is why a sealed room you are
 * not in cannot come back from it at all.
 *
 * WHAT A ROW SAYS, AND IT SAYS ONE NUMBER NOW (docs/5 § D260805-1500). How many open
 * letters have been written in the room, and whether you are in it. This paragraph
 * used to say the opposite — "no member count, no post count, nothing a room could be
 * ranked by" — and the decision that reversed it is the one to read before reversing
 * it again, because the argument it accepts is narrow: a room is a place, the figure
 * counts LETTERS rather than anybody's engagement, and no number in this product
 * attaches to a person. What has not moved an inch is I5 and § D260801-1119 — no like
 * count, no comment count, no follower count, no send-on count, for anyone including
 * the author, anywhere.
 *
 * WHAT A ROW STILL REFUSES TO SAY: how many PEOPLE are in a room, and anything about
 * "activity" or "today". A member count is the follower count with a different noun,
 * and a room that is busy *this week* is a trend — the shelf height is a slow number
 * by construction, which is the property that makes it a description of a place
 * rather than a leaderboard position.
 *
 * IT ANSWERS BEFORE IT IS TYPED INTO (docs/5 § D260805-1233), AND IT DOES NOT STOP.
 * Tapping the field opens the panel on the whole directory, most written-in first, and
 * the next page arrives as the last row comes into view (§ D260805-1500). That replaced
 * a handful capped at `SPACE_OFFER` — the cap was the mechanism that kept a list of
 * rooms from being a browsing surface, and browsing is what it is for now. The one
 * thing that has NOT changed is that narrowing never re-ranks: the order does not
 * depend on the query, so typing removes rows and never moves one out from under a
 * thumb (`GET /spaces/search` is where that is enforced).
 *
 * A ROOM THAT IS NOT THERE CAN BE ASKED FOR (docs/5 § D260805-1500b). When a query
 * matches nothing the panel offers to pass the name on, once per person per name, and
 * nothing about who else asked is ever shown — see `SuggestSpace`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Below the threshold at which the list feels like it lags the field. */
const SEARCH_DEBOUNCE_MS = 250;

/** Marks a row the arrow keys can reach — see `useSpaceSearch`. */
const ROW_ATTR = 'data-space-row';

/**
 * THE FIELD AND THE PANEL ARE ONE CONTROL IN TWO PLACES, so the state is a hook
 * rather than props threaded through a screen. The field sits in the Feed's
 * `TopRow` and the panel replaces the column underneath it — different corners of
 * the tree, one behaviour: what you typed, what is showing, and which row the
 * keyboard is on.
 *
 * WHY THE PANEL IS NOT TIED TO FOCUS. The obvious build opens it on focus and closes
 * it on blur, and that one is broken on a phone: tapping a row blurs the field, and a
 * panel that unmounts on blur takes the link out from under the finger before the tap
 * lands. So it opens on focus and closes only on something somebody did on purpose —
 * the X, Escape, or arriving in a room. The X is drawn even over an empty field for
 * exactly this reason (`SearchField`): the way back to the feed has to be visible, and
 * it has to be reachable by a thumb.
 *
 * THE KEYBOARD MOVES FOCUS INTO THE ROWS RATHER THAN A HIGHLIGHT ALONG THEM. Down
 * from the field focuses the first row, Down and Up walk them, Up from the first
 * returns to the field, and Enter is a link being followed — which is what it is. The
 * alternative, `aria-activedescendant` over `role="option"` rows, means telling a
 * screen reader that six links are options in a listbox and then reimplementing what
 * Enter on a link already does.
 */
export function useSpaceSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const typed = query.trim().length > 0;
  const q = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS);

  const joined = useJoinedSpaces();

  /*
   * ONE QUERY FOR BROWSING AND FOR SEARCHING (docs/5 § D260805-1500). An empty `q` is
   * the whole directory rather than an empty answer, so there is no second list
   * assembled on this side out of `GET /spaces` and sliced to a handful — which is
   * what this hook did until the panel became browsable, and it meant the unsearched
   * rows and the searched rows had two orders, two shapes and two chances to disagree
   * about the count. Typing narrows a list that is already correct.
   *
   * IT IS NOT READ UNTIL THE PANEL IS OPEN (`open` is the `enabled` flag), because
   * counting the letters in every room is the heaviest read in the product and this
   * field is on the Feed for everybody, most of whom never tap it.
   *
   * DEBOUNCED, BECAUSE THE QUERY IS A REQUEST. Typing "slow cooking" is twelve
   * keystrokes, and twelve `LIKE` queries to render the twelfth one is the browser
   * racing itself over rooms that are not going to change.
   *
   * The FIELD is not debounced — what you typed appears immediately, because it is
   * yours. Only the fetch waits.
   */
  const results = useSpaceRooms(q, open);

  /* THE PAGES, FLATTENED. `useInfiniteQuery` keeps them separate because it has to
     page them; nothing below this line cares where the boundaries were. */
  const matches = useMemo(
    () => results.data?.pages.flatMap((p) => p.spaces),
    [results.data],
  );

  const rows = () =>
    Array.from(
      panel.current?.querySelectorAll<HTMLElement>(`[${ROW_ATTR}]`) ?? [],
    );

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  return {
    /** Whether the panel is standing in for the screen underneath it. */
    open,
    close,
    /** Everything `SpaceSearchField` needs, and nothing a caller has to assemble. */
    field: {
      value: query,
      onValue: setQuery,
      inputRef: field,
      onFocus: () => setOpen(true),
      onClear: close,
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          rows()[0]?.focus();
        } else if (e.key === 'Escape') {
          /* Escape empties a field that has something in it and dismisses one that
             does not — the X's two steps, on the key that means the same thing. */
          if (query) setQuery('');
          else close();
        } else if (e.key === 'Enter') {
          /* The first row, because the list is not re-ranked between keystrokes and
             so Enter goes where the eye already is. */
          const first = matches?.[0];
          if (first) {
            e.preventDefault();
            close();
            void navigate({ to: '/spaces/$slug', params: { slug: first.slug } });
          }
        }
      },
    },
    /** Everything `SpaceResults` needs. */
    panel: {
      containerRef: panel,
      query,
      typed,
      matches,
      joined,
      pending: results.isPending,
      failed: results.isError,
      retry: () => void results.refetch(),
      /** The next page, asked for by the bottom of the list coming into view. */
      more: Boolean(results.hasNextPage) && !results.isFetchingNextPage,
      loadingMore: results.isFetchingNextPage,
      onReachEnd: () => void results.fetchNextPage(),
      onRowKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        e.preventDefault();
        const all = rows();
        const here = all.indexOf(e.currentTarget);
        const next = e.key === 'ArrowDown' ? here + 1 : here - 1;
        if (next < 0) field.current?.focus();
        else all[next]?.focus();
      },
      onChosen: close,
    },
  };
}

export type SpaceSearch = ReturnType<typeof useSpaceSearch>;

/**
 * The field itself, shaped to sit in a `TopRow`.
 *
 * IDENTICAL TO THE LOFT'S, deliberately — and identical now because both are one
 * component (`SearchField`) rather than because two files were kept in step by hand.
 * The two tabs' opening rows have to measure the same (see `TopRow`), and a field
 * that looks like the Loft's should behave like it: you type, and what is under it
 * narrows. The difference is what it searches, which is what the label is for.
 */
export function SpaceSearchField({ search }: { search: SpaceSearch }) {
  return (
    <SearchField
      {...search.field}
      label="Search spaces"
      /*
        NO ARIA BEYOND THE LABEL, AND THAT IS THE CONSIDERED VERSION RATHER THAN THE
        LAZY ONE. This carried `aria-expanded` and `aria-controls` for one commit:
        both are the combobox pattern, `aria-expanded` is not allowed on a
        searchbox, and claiming `role="combobox"` would mean claiming its popup is a
        `listbox` of `option`s. It is not — it is a list of links to rooms, announced
        as links, followed by Enter like any other link (see `useSpaceSearch`), and
        the panel replaces the page's whole column rather than floating over it. What
        a screen reader gets is what is actually there: a search field, then a list
        called *Spaces*, each row saying whether you are in it.
      */
    />
  );
}

/**
 * What the field found — or, before anything is typed, what it offers: rooms as
 * ROWS, divided by hairlines.
 *
 * The Loft's list vocabulary (`CorrespondenceRow`), because it is the same kind of
 * object — a list of places to go, on the app's own ground, where a gap between
 * things would mean cards. It is also what keeps the search from looking like the
 * feed it temporarily replaces: letters are sheets, results are rows, and you can
 * tell which one you are looking at without reading a word.
 */
export function SpaceResults({ panel }: { panel: SpaceSearch['panel'] }) {
  const { query, typed, matches, joined, pending, failed } = panel;

  return (
    <div id="space-panel" ref={panel.containerRef}>
      {!matches && pending ? (
        <Loading kind="rows" count={3} />
      ) : !matches && failed ? (
        <Failed onRetry={panel.retry} />
      ) : !matches?.length ? (
        /* One clause, and only when something was actually looked for. A directory
           that came back empty is a database with no rooms in it, which is not a
           sentence to write to anybody. */
        typed ? (
          /* KEYED ON THE NAME, so backspacing and typing a different one offers
             again instead of reporting the previous name's outcome. The alternative
             is reconciling a mutation's state against a prop by hand, which is the
             `key` prop's whole job. */
          <SuggestSpace key={query.trim()} name={query.trim()} />
        ) : null
      ) : (
        <>
          <SpaceList
            spaces={matches}
            joined={joined}
            onRowKeyDown={panel.onRowKeyDown}
            onChosen={panel.onChosen}
          />
          {/* MORE ARRIVES BY SCROLLING (`More`). No root: this list is in the app's
              main column, and an observer against the viewport already accounts for
              the shell's scroller clipping the sentinel. */}
          <More enabled={panel.more} onReach={panel.onReachEnd} />
          {panel.loadingMore && <Loading kind="rows" count={2} />}
        </>
      )}
    </div>
  );
}

/**
 * A ROOM THAT IS NOT THERE, AND THE OFFER TO PASS THE NAME ON
 * (docs/5 § D260805-1500b).
 *
 * ONE LINE AND ONE CONTROL, which is what `Note` is for and what the empty state
 * already was — the button is the only thing added. No paragraph about what happens
 * next, no "we read every suggestion", no thanks: the copy rule is that a surface
 * labels rather than explains (docs/1 § Copy), and reassurance is the specific thing
 * it names. *Suggest it* is two words and its object is the name in the line above.
 *
 * WHAT IT BECOMES IS A FACT, NOT A DISABLED BUTTON, which is the pattern *Joined*
 * already set on a room: the line changes to say the name was suggested and the
 * control goes away. There is nothing to press and therefore nothing to explain.
 *
 * NOTHING ABOUT ANYBODY ELSE IS SHOWN, and that is a rule rather than an omission. No
 * "you and 11 others", no list of pending rooms, no queue position. A visible tally of
 * wishes is a petition, and a petition is a thing to organise — the count exists only
 * where we read it (`npm run db:suggestions`).
 *
 * ASKING TWICE IS NOT AN ERROR. The server is idempotent per person per name, so a
 * second tap on a slow connection lands on a 200 and this component never has to
 * explain a conflict it caused. The two refusals it CAN get are a name that already
 * exists and one suggestion too many for today, and both arrive as the server's own
 * sentence — rendered as the line, because a room that turns out to exist is an answer
 * to the search rather than an error in a form.
 */
function SuggestSpace({ name }: { name: string }) {
  const suggest = useMutation({ mutationFn: () => suggestSpace(name) });

  if (suggest.isError) return <Note>{apiMessage(suggest.error)}</Note>;
  if (suggest.isSuccess) return <Note>Suggested “{name}”.</Note>;

  return (
    <Note
      action={
        <Button
          variant="outline"
          className="rounded-full"
          disabled={suggest.isPending}
          onClick={() => suggest.mutate()}
        >
          Suggest it
        </Button>
      }
    >
      No space called “{name}”.
    </Note>
  );
}

/**
 * Rooms as rows.
 *
 * EXPORTED, BECAUSE THE FEED'S EMPTY STATE IS THE SAME LIST. A new account's feed has
 * nothing in it and the honest thing to offer is the rooms — which is this, exactly
 * this, rather than a second row shape three files away that would drift from it (see
 * `FeedScreen`). The row leads to the room, where the join control lives; a Join
 * button on a search result would be a second entrance to a decision the room's own
 * screen already draws.
 */
export function SpaceList({
  spaces,
  joined,
  onRowKeyDown,
  onChosen,
}: {
  spaces: SpaceListing[];
  joined: ReadonlySet<string>;
  /** Set by the search panel, which walks these rows with the arrow keys. */
  onRowKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /** Set by the search panel, which dismisses itself once you have gone. */
  onChosen?: () => void;
}) {
  return (
    <ul aria-label="Spaces" className="divide-y divide-border/60">
      {spaces.map((space) => {
        const Icon = spaceIcon(space);
        const inIt = joined.has(space.id);
        return (
          <li key={space.id}>
            <Link
              to="/spaces/$slug"
              params={{ slug: space.slug }}
              {...{ [ROW_ATTR]: '' }}
              onKeyDown={onRowKeyDown}
              onClick={onChosen}
              /* ONE LABEL FOR THE WHOLE ROW, because everything to the right of the
                 name is `aria-hidden` and would otherwise be read as loose fragments
                 after it — "Walking", "Joined", "1.5k". The figure is spelled out in
                 words here rather than abbreviated: "1.5k" is a glance, and a screen
                 reader saying "one point five kay" is a puzzle.
                 `focus-visible:ring-inset` because the box fills the width of the
                 screen and an outset ring is drawn off both edges. */
              aria-label={[
                space.title,
                inIt ? 'joined' : null,
                space.letters > 0 ? lettersLabel(space.letters) : null,
              ]
                .filter(Boolean)
                .join(', ')}
              className="flex items-center gap-3 px-4 py-3 outline-none transition-colors hover:bg-muted/50 focus-visible:ring-3 focus-visible:ring-inset focus-visible:ring-ring/50"
            >
              {/* The room's own glyph, from `lib/space` — the same one the
                  composer, the foot of a card and the profile's filter draw, so a
                  padlock means the same thing everywhere. On a muted disc here
                  because a bare 16px icon at the start of a 48px row reads as
                  punctuation rather than as the room's mark. */}
              <span
                aria-hidden
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
              >
                <Icon className="size-4" />
              </span>

              {/* `bdi`: a room's title is content, in an unknown script
                  (docs/8 § G). */}
              <bdi className="min-w-0 flex-1 truncate text-[15px] font-medium">
                {space.title}
              </bdi>

              {/* WHAT THE ROOM IS AND WHAT IT IS TO YOU, in ONE muted cluster at the
                  end of the row rather than a badge each on its own line (which is
                  the layout rule in CLAUDE.md, and it is what two separately
                  positioned labels turn into on a narrow phone).

                  `aria-hidden` on the cluster because the row's own label carries
                  both, in words.

                  THE COUNT IS ABSENT AT ZERO, not drawn as `0`. A directory of a
                  freshly seeded database is fifty rooms with nothing in them, and a
                  column of zeroes is worse than no column: it reads as a broken
                  number rather than as an empty shelf. A room with no letters yet is
                  simply a name, which is what every row was before § D260805-1500. */}
              <span
                aria-hidden
                className="flex shrink-0 items-center gap-1.5 text-[12px] text-muted-foreground"
              >
                {inIt && <span>Joined</span>}
                {inIt && space.letters > 0 && (
                  <span className="opacity-40">·</span>
                )}
                {space.letters > 0 && (
                  /* `tabular-nums` so the figures form a column down the edge of the
                     list instead of shuffling by a pixel per row. `bdi` because a
                     Latin-digit count sits inside an Arabic row and the neutral
                     characters around it would otherwise reorder. */
                  <bdi className="tabular-nums">{formatCount(space.letters)}</bdi>
                )}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The count in words, for the row's own label.
 *
 * NOT `Intl.PluralRules`, deliberately: this is one English string with one plural
 * form, and the app has no translation layer yet — a plural-rules call here would
 * imply the surrounding sentence is localised, which it is not. `formatCount` is the
 * localised half, because a NUMBER can be formatted for a reader whose UI copy is
 * still English.
 */
function lettersLabel(n: number): string {
  return n === 1 ? '1 letter' : `${formatCount(n)} letters`;
}

/** The server's own sentence, or a plain one if the request never got that far. */
function apiMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : "Couldn't do that.";
}

/* `useDebounced` LIVED HERE AND IN `CardFields`, and it is one copy in
   `lib/debounced.ts` now. The note it carried — "the only thing in this product that
   types into a network is this field and the loft's" — stopped being true when the
   GIF picker got a search field (docs/5 § D260805-1221), which is exactly the moment
   a third hand-written copy would have appeared. */
